// THE LANGUAGE IS A CHOICE, and until this file existed it was not one.
//
// `appLocale()` resolved a locale from storage, then from `navigator.languages`, then
// from the default — three sources, and a reader who disagreed with all three had no
// way to say so. `rememberLocale` was exported and called by nothing but its own test;
// `locale.label`, `locale.tr` and `locale.en` sat in the catalogue with no consumer.
// The browser decided once, permanently, and the only way to see the other language was
// to change an operating-system setting.
//
// That was survivable while the default was the market's language and the other half was
// a reference for translators. It is not survivable now that the default is the SOURCE:
// "Turkish is chosen rather than assumed" is only true if there is something to choose
// with.
//
// # State, not a reload
//
// The obvious implementation is `rememberLocale(next); location.reload()`. It is wrong
// for a reason this product is careful about elsewhere: a reload throws away every open
// pane and every half-typed form, so switching language would silently discard work —
// and `DiscardGuard` exists precisely because losing typed input without asking is not
// acceptable here. Holding the locale in state re-renders the tree and keeps the form.
//
// The choice is written to storage in the same act, so it survives the next visit. The
// write is allowed to fail (private mode): the choice then lasts for this page, which is
// a smaller loss than an exception thrown out of a language switcher.

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { type Locale, LocaleProvider } from "./index";
import { appLocale, rememberLocale } from "./resolve";

/** Changing the language. `null` where nothing is holding the choice — the careers
 * surface, whose locale is the URL's and not a preference. */
const ChoiceContext = createContext<((locale: Locale) => void) | null>(null);

/**
 * Holds the reader's language and lets them change it.
 *
 * Wraps `LocaleProvider` rather than replacing it: the provider's job is to supply a
 * locale to the tree and keep `<html lang>` truthful, and that job is the same whether
 * the locale came from a preference, from a URL segment, or from a test.
 */
export function LocaleChoiceProvider({
  children,
  initialLocale,
  loadLocale,
}: {
  readonly children: ReactNode;
  /** Already resolved and installed by an entry. Tests and smaller surfaces may omit it. */
  readonly initialLocale?: Locale;
  /** Ensure a language's messages are installed before it becomes visible. */
  readonly loadLocale?: (locale: Locale) => Promise<void>;
}) {
  // Resolved ONCE, at mount. Re-reading it on every render would let a change to
  // `navigator.languages` mid-session override a choice the reader just made.
  const [locale, setLocale] = useState<Locale>(() => initialLocale ?? appLocale());
  // A reader can make two choices before the first chunk returns (keyboard activation and a
  // second menu open are enough). Only the latest intent may commit when promises resolve out
  // of order, or a slow first download changes the language back underneath them.
  const requested = useRef(0);

  const choose = useCallback(
    (next: Locale) => {
      if (next === locale) {
        // Choosing the language already on screen is still an INTENT. In particular it
        // cancels a slower download for another language; otherwise that older promise
        // can resolve later and change the screen after the reader chose to stay here.
        requested.current += 1;
        return;
      }
      const request = ++requested.current;
      const loading = loadLocale?.(next) ?? Promise.resolve();
      void loading
        .then(() => {
          if (requested.current !== request) return;
          rememberLocale(next);
          setLocale(next);
        })
        .catch((error: unknown) => {
          // Keep the complete current language on screen. A failed optional chunk is reported
          // to diagnostics and never commits a locale whose messages are absent.
          console.error("riksu: the selected language could not be loaded", error);
        });
    },
    [loadLocale, locale],
  );

  const value = useMemo(() => choose, [choose]);

  return (
    <ChoiceContext.Provider value={value}>
      <LocaleProvider locale={locale}>{children}</LocaleProvider>
    </ChoiceContext.Provider>
  );
}

/**
 * How to change the language, or `null` where the language is not the reader's to
 * change.
 *
 * `null` rather than a no-op function: a switcher that renders and does nothing is worse
 * than one that does not render, and the caller can only tell the difference if the
 * absence is representable.
 */
export function useLocaleChoice(): ((locale: Locale) => void) | null {
  return useContext(ChoiceContext);
}
